"use client"

import * as React from "react"

import { createPersistedStore, usePersistedStore } from "@/lib/persisted-store"
import {
  defaultClassificationLookups,
  levelsOf,
  moved,
  newLevelValue,
  reordered,
  newTypeId,
  withLevels,
  withNewLevels,
  type ClassificationLookups,
  type ClassificationKind,
  type CustomClassificationType,
  type LevelListKey,
  type LookupLevel,
  type NewLevel,
} from "@/lib/classification/lookups"

export const CLASSIFICATION_LOOKUPS_STORE_KEY = "shell:classification-lookups:v1"

const store = createPersistedStore<ClassificationLookups>(CLASSIFICATION_LOOKUPS_STORE_KEY, defaultClassificationLookups)

/** Bloom's, Difficulty and the admin's own types: one store for Settings and the question editor. */
export function useClassificationLookups() {
  const [stored, set] = usePersistedStore(store)
  // A list saved before custom types existed has none.
  const lookups = React.useMemo<ClassificationLookups>(() => ({ ...stored, custom: stored.custom ?? [] }), [stored])

  const updateLevel = React.useCallback(
    (key: LevelListKey, value: string, patch: Partial<Omit<LookupLevel, "value">>) =>
      set((current) =>
        withLevels(
          { ...current, custom: current.custom ?? [] },
          key,
          levelsOf({ ...current, custom: current.custom ?? [] }, key).map((level) => (level.value === value ? { ...level, ...patch } : level)),
        ),
      ),
    [set],
  )
  /**
   * A new level, at `at` (a scale's position) or last. Returns the value it
   * stores, so its course mapping can be saved under it.
   */
  const addLevel = React.useCallback(
    (key: LevelListKey, label: string, description = "", options: { active?: boolean; at?: number; score?: number } = {}): string => {
      const current = store.get()
      const value = newLevelValue(levelsOf({ ...current, custom: current.custom ?? [] }, key), label)
      set((latest) => {
        const base = { ...latest, custom: latest.custom ?? [] }
        const levels = [...levelsOf(base, key)]
        const level = {
          value,
          label: label.trim(),
          description,
          active: options.active ?? true,
          ...(options.score !== undefined ? { score: options.score } : {}),
        }
        levels.splice(Math.min(Math.max(options.at ?? levels.length, 0), levels.length), 0, level)
        return withLevels(base, key, levels)
      })
      return value
    },
    [set],
  )
  /** Several levels in one go, from the inline add rows. */
  const addLevels = React.useCallback(
    (key: LevelListKey, rows: readonly NewLevel[], byScore: boolean) =>
      set((current) => {
        const base = { ...current, custom: current.custom ?? [] }
        return withLevels(base, key, withNewLevels(levelsOf(base, key), rows, byScore))
      }),
    [set],
  )
  /** Put one level at a position on its scale. */
  const placeLevel = React.useCallback(
    (key: LevelListKey, value: string, to: number) =>
      set((current) => {
        const base = { ...current, custom: current.custom ?? [] }
        const levels = levelsOf(base, key)
        return withLevels(base, key, reordered(levels, levels.findIndex((l) => l.value === value), to))
      }),
    [set],
  )
  const moveLevel = React.useCallback(
    (key: LevelListKey, value: string, direction: "up" | "down") =>
      set((current) => {
        const base = { ...current, custom: current.custom ?? [] }
        const levels = levelsOf(base, key)
        return withLevels(base, key, moved(levels, levels.findIndex((level) => level.value === value), direction))
      }),
    [set],
  )
  const removeLevel = React.useCallback(
    (key: LevelListKey, value: string) =>
      set((current) => {
        const base = { ...current, custom: current.custom ?? [] }
        return withLevels(base, key, levelsOf(base, key).filter((level) => level.value !== value))
      }),
    [set],
  )
  /** Drop one level where another is. */
  const reorderLevel = React.useCallback(
    (key: LevelListKey, dragValue: string, targetValue: string) =>
      set((current) => {
        const base = { ...current, custom: current.custom ?? [] }
        const levels = levelsOf(base, key)
        return withLevels(
          base,
          key,
          reordered(levels, levels.findIndex((l) => l.value === dragValue), levels.findIndex((l) => l.value === targetValue)),
        )
      }),
    [set],
  )
  /** Drop one type where another is. `scopes` is the order on screen now. */
  const reorderType = React.useCallback(
    (scopes: readonly string[], drag: string, target: string) =>
      set((current) => ({ ...current, typeOrder: reordered(scopes, scopes.indexOf(drag), scopes.indexOf(target)) })),
    [set],
  )
  /** Move a type one place in the overview and panel. `scopes` is the order on screen now. */
  const moveType = React.useCallback(
    (scopes: readonly string[], scope: string, direction: "up" | "down") =>
      set((current) => ({ ...current, typeOrder: moved(scopes, scopes.indexOf(scope), direction) })),
    [set],
  )
  /** A hierarchy passes the dimension its categories live in. */
  const addType = React.useCallback(
    (name: string, description = "", shape: { kind?: ClassificationKind; dimensionId?: string } = {}): string => {
      const id = newTypeId(store.get().custom ?? [], name)
      const kind = shape.kind ?? "scale"
      set((current) => ({
        ...current,
        custom: [
          ...(current.custom ?? []),
          {
            id,
            name: name.trim(),
            description: description.trim(),
            kind,
            structure: kind === "tree" ? "tree" : "list",
            dimensionId: shape.dimensionId,
            levels: [],
          },
        ],
      }))
      return id
    },
    [set],
  )
  const updateType = React.useCallback(
    /** A list type's kind can change between scale, single choice and tags; see SWITCHABLE_KINDS. */
    (id: string, patch: Partial<Pick<CustomClassificationType, "name" | "description" | "kind" | "dimensionId">>) =>
      set((current) => ({
        ...current,
        custom: (current.custom ?? []).map((type) => (type.id === id ? { ...type, ...patch } : type)),
      })),
    [set],
  )
  const removeType = React.useCallback(
    (id: string) => set((current) => ({ ...current, custom: (current.custom ?? []).filter((type) => type.id !== id) })),
    [set],
  )
  return { lookups, updateLevel, addLevel, addLevels, placeLevel, moveLevel, reorderLevel, removeLevel, moveType, reorderType, addType, updateType, removeType }
}
